/**
 * The `hours` control kind — README §6: "seven day rows: label, open
 * toggle, from/to times." One PUT per change, sending the whole
 * `WeekHours` object back (the ops-layer field is a single JSON value, not
 * seven independent ones) — see docs/design-handoff/data/settings-model.json
 * `v.hours` and packages/shared/src/config.ts's matching default shape.
 *
 * Task 33: this block sits inside a `FieldRow`'s ordinary control column
 * (see FieldRow.tsx's `FIELD_ROW_GRID_CLASS`) rather than spanning the full
 * row — at ~360px (32px day label + 40px toggle + two 112px time inputs +
 * gaps) it fits comfortably inside the ~436px control column the 260px/720px
 * split leaves, so it lines up on the same left edge as every other kind of
 * control in the section instead of being a one-off exception. Internally,
 * every one of the seven day rows now shares `HOURS_ROW_GRID_CLASS` — a grid
 * with fixed-width day-label/toggle/from/to tracks — so the toggle and both
 * time inputs land at the same x on every day regardless of which days
 * happen to be on/off.
 *
 * Task 35: this block is a *block* control, not a single element like a
 * toggle or an input, so "control right" (README §6) needed a deliberate
 * call rather than falling out of FieldRow's `justify-end` for free. The
 * choice made here: right-justify the whole seven-row block as one unit,
 * the same way the logo section's two wells are right-justified as one unit
 * (see LogoField.tsx) — not "right-justify each day row independently"
 * (which is meaningless, since all seven rows are already the same width as
 * each other via `HOURS_ROW_GRID_CLASS`) and not "stretch the block to fill
 * the control column and left-align the day rows inside it" (which is what
 * the old `w-full` did, and is exactly the raggedness this task exists to
 * remove — it put the block's *left* edge, not its right edge, at a fixed
 * x). Dropping `w-full` lets this div size to its own content (~306px, well
 * under the ~436px control column) so FieldRow's `justify-end` pushes the
 * whole block flush against the row's right edge — the same edge every
 * single-input control now shares.
 */
import type { ConfigField } from '@communicator/shared'
import { Toggle } from '../ui'
import type { DayHours, WeekHours } from '../../store'

const DAY_ORDER: Array<keyof WeekHours> = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']
const DAY_LABEL: Record<keyof WeekHours, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',
}

/** Fixed-width day-label / toggle / from / "to" / to-time tracks, shared by
 * every day row so the seven rows align with each other regardless of a
 * given day's label width or on/off state. */
/**
 * TRACK WIDTHS MUST FOLLOW THE CONTROLS THEY HOLD.
 *
 * The toggle track was `26px`, which was the width of the OLD toggle
 * (14x26). When the toggle was rebuilt to the design's 22x40, this grid was
 * not updated with it — so every open day rendered its toggle crushed into a
 * column 14px too narrow, which is what Andrew saw as "the day toggles are
 * messed up". Now 40px, matching `Toggle`'s own `w-[40px]`; the test in
 * settings.test.tsx compares the two so they cannot drift apart again.
 *
 * The time inputs were `92px`, which fits "08:00" but NOT the "08:00 AM" a
 * 12-hour locale renders, nor the clock button the browser draws inside a
 * `type="time"` field — so they displayed as "08:00 AI" with the M sliced
 * off. 112px carries both.
 *
 * Total: 32 + 40 + 112 + 16 + 112 + four 12px gaps = 360px, still inside the
 * ~436px control column the 260/720 split leaves, so the block goes on
 * sitting flush with every other control's right edge.
 */
export const HOURS_ROW_GRID_CLASS = 'grid grid-cols-[32px_40px_112px_16px_112px] items-center gap-3'

export interface HoursFieldProps {
  field: ConfigField
  value: WeekHours | undefined
  locked: boolean
  onCommit: (id: string, value: unknown) => Promise<void>
}

export function HoursField({ field, value, locked, onCommit }: HoursFieldProps) {
  const hours = value ?? (field.default as WeekHours)

  function patchDay(day: keyof WeekHours, patch: Partial<DayHours>) {
    const nextDay: DayHours = { ...hours[day], ...patch }
    const next: WeekHours = { ...hours, [day]: nextDay }
    void onCommit(field.id, next)
  }

  return (
    <div className="flex flex-col gap-1.5" data-testid={`hours-${field.id}`}>
      {DAY_ORDER.map((day) => {
        const d = hours[day]
        return (
          <div key={day} className={HOURS_ROW_GRID_CLASS} data-testid={`hours-row-${day}`}>
            <span className="text-[12px] text-mid">{DAY_LABEL[day]}</span>
            <Toggle
              checked={d.on}
              disabled={locked}
              aria-label={`${DAY_LABEL[day]} open`}
              onChange={(checked) =>
                patchDay(day, checked ? { on: true, from: d.from ?? '08:00', to: d.to ?? '17:00' } : { on: false })
              }
            />
            <input
              type="time"
              value={d.from ?? ''}
              disabled={locked || !d.on}
              aria-label={`${DAY_LABEL[day]} from`}
              onChange={(e) => patchDay(day, { from: e.target.value })}
              className="h-[28px] w-[112px] rounded-[6px] border border-subtle bg-card px-1.5 text-[12px] text-hi disabled:opacity-50"
            />
            <span className="text-center text-[11px] text-lo">to</span>
            <input
              type="time"
              value={d.to ?? ''}
              disabled={locked || !d.on}
              aria-label={`${DAY_LABEL[day]} to`}
              onChange={(e) => patchDay(day, { to: e.target.value })}
              className="h-[28px] w-[112px] rounded-[6px] border border-subtle bg-card px-1.5 text-[12px] text-hi disabled:opacity-50"
            />
          </div>
        )
      })}
    </div>
  )
}
